/**
 * React Flow canvas for a single process.
 *
 * Layout: left → right with swim lanes (one lane per accountable seat).
 * On data load, computes a topological LR layout and lane bands; users can
 * drag manually after that, and "Auto-layout" recomputes from edges + lanes.
 *
 * Saves push the full state to PUT /v4/processes/:id/graph in one transaction.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type Connection,
  type OnSelectionChangeParams,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '@/components/ui/button';
import { Save, Plus, LayoutGrid } from 'lucide-react';
import { useProcessGraph, useSaveGraph } from '../hooks';
import { nodeTypes } from './nodes';
import type { ProcessNodeType, ProcessGraph } from '../types';

interface ProcessCanvasProps {
  processId: string;
  onSelectStepId?: (stepId: string | null) => void;
}

let tempCounter = 0;
const newTempId = () => `tmp-${Date.now()}-${++tempCounter}`;

// Layout constants for LR + swim lanes.
const LANE_HEIGHT = 180;
const LANE_HEADER_WIDTH = 140;
const COLUMN_WIDTH = 260;
const NODE_HEIGHT = 80;
const TOP_PADDING = 40;
const LEFT_PADDING = LANE_HEADER_WIDTH + 30;

const UNASSIGNED = 'Unassigned';

/**
 * Topological levels: assign each step an X-layer. Level = longest predecessor chain.
 * Returns Map<stepId, level>. Cycles → arbitrary order, no infinite loop.
 */
function computeLevels(stepIds: string[], edges: Array<{ source: string; target: string }>): Map<string, number> {
  const incoming = new Map<string, string[]>();
  const outgoing = new Map<string, string[]>();
  stepIds.forEach((id) => { incoming.set(id, []); outgoing.set(id, []); });
  edges.forEach((e) => {
    if (incoming.has(e.target)) incoming.get(e.target)!.push(e.source);
    if (outgoing.has(e.source)) outgoing.get(e.source)!.push(e.target);
  });

  const level = new Map<string, number>();
  const queue: string[] = stepIds.filter((id) => (incoming.get(id) || []).length === 0);
  queue.forEach((id) => level.set(id, 0));

  // BFS-style propagation; multiple visits allowed, take max level seen.
  const seen = new Set<string>();
  let head = 0;
  let safety = stepIds.length * 5;
  while (head < queue.length && safety-- > 0) {
    const id = queue[head++];
    seen.add(id);
    const cur = level.get(id) || 0;
    for (const nx of outgoing.get(id) || []) {
      const next = Math.max(level.get(nx) ?? -1, cur + 1);
      if (level.get(nx) !== next) {
        level.set(nx, next);
        queue.push(nx);
      }
    }
  }

  // Anything still without a level (cycle / disconnected) → assign 0.
  stepIds.forEach((id) => { if (!level.has(id)) level.set(id, 0); });
  return level;
}

/**
 * Compute lane assignments from stepSeats. First accountable seat wins, else
 * first seat regardless of role, else "Unassigned".
 */
function computeLaneByStep(graph: ProcessGraph): Map<string, string> {
  const map = new Map<string, string>();
  for (const s of graph.steps) {
    const seats = graph.stepSeats.filter((x) => x.step_id === s.id);
    const accountable = seats.find((x) => x.role === 'accountable');
    map.set(s.id, accountable?.seat_title || seats[0]?.seat_title || UNASSIGNED);
  }
  return map;
}

/**
 * Order lanes: prefer order of first appearance among the steps' levels.
 * Unassigned always last.
 */
function orderLanes(stepIds: string[], laneByStep: Map<string, string>, levels: Map<string, number>): string[] {
  const seen = new Map<string, number>(); // lane → first level
  for (const id of [...stepIds].sort((a, b) => (levels.get(a) || 0) - (levels.get(b) || 0))) {
    const lane = laneByStep.get(id) || UNASSIGNED;
    if (!seen.has(lane)) seen.set(lane, levels.get(id) || 0);
  }
  const lanes = Array.from(seen.keys());
  // Move "Unassigned" to the end.
  return [...lanes.filter((l) => l !== UNASSIGNED), ...lanes.filter((l) => l === UNASSIGNED)];
}

interface AutoLayoutResult {
  positionByStep: Map<string, { x: number; y: number }>;
  laneNodes: Node[];
  contentWidth: number;
  contentHeight: number;
}

function computeAutoLayout(graph: ProcessGraph): AutoLayoutResult {
  const stepIds = graph.steps.map((s) => s.id);
  const levels = computeLevels(stepIds, graph.edges.map((e) => ({ source: e.source_step_id, target: e.target_step_id })));
  const laneByStep = computeLaneByStep(graph);
  const lanes = orderLanes(stepIds, laneByStep, levels);
  const laneIndex = new Map(lanes.map((l, i) => [l, i]));

  const maxLevel = Math.max(0, ...Array.from(levels.values()));
  const colCount = maxLevel + 2; // +1 col for buffer right
  const rowCount = lanes.length;

  // Within each lane × column, stack nodes vertically if multiple share a cell.
  const cellOccupancy = new Map<string, number>(); // "lane|col" → count
  const positionByStep = new Map<string, { x: number; y: number }>();
  for (const s of graph.steps) {
    const lane = laneByStep.get(s.id) || UNASSIGNED;
    const col = levels.get(s.id) || 0;
    const cellKey = `${lane}|${col}`;
    const stackIndex = cellOccupancy.get(cellKey) || 0;
    cellOccupancy.set(cellKey, stackIndex + 1);

    const laneI = laneIndex.get(lane) || 0;
    const x = LEFT_PADDING + col * COLUMN_WIDTH;
    const yBase = TOP_PADDING + laneI * LANE_HEIGHT + (LANE_HEIGHT / 2 - NODE_HEIGHT / 2);
    const y = yBase + (stackIndex - 0.5 * (cellOccupancy.get(cellKey)! - 1)) * (NODE_HEIGHT + 16);
    positionByStep.set(s.id, { x, y });
  }

  const contentWidth = LEFT_PADDING + colCount * COLUMN_WIDTH + 80;
  const contentHeight = TOP_PADDING + rowCount * LANE_HEIGHT + 60;

  const laneNodes: Node[] = lanes.map((lane, i) => ({
    id: `lane-${i}`,
    type: 'lane',
    position: { x: 0, y: TOP_PADDING + i * LANE_HEIGHT },
    data: { title: lane, width: contentWidth, height: LANE_HEIGHT - 16 },
    draggable: false,
    selectable: false,
    style: { zIndex: -1 },
  }));

  return { positionByStep, laneNodes, contentWidth, contentHeight };
}

export function ProcessCanvas({ processId, onSelectStepId }: ProcessCanvasProps) {
  const graphQuery = useProcessGraph(processId);
  const saveMutation = useSaveGraph(processId);

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [dirty, setDirty] = useState(false);

  // Pre-compute display extras from the API payload.
  const enrich = useMemo(() => {
    const g = graphQuery.data;
    if (!g) return null;
    const kpiByStep = new Map<string, number>();
    g.stepKpis.forEach((k) => kpiByStep.set(k.step_id, (kpiByStep.get(k.step_id) || 0) + 1));
    const seatsByStep = new Map<string, Array<{ seat_title: string; role: string }>>();
    g.stepSeats.forEach((s) => {
      const arr = seatsByStep.get(s.step_id) || [];
      arr.push({ seat_title: s.seat_title, role: s.role });
      seatsByStep.set(s.step_id, arr);
    });
    return { kpiByStep, seatsByStep };
  }, [graphQuery.data]);

  // Auto-layout LR + lanes on load. If a step already has non-zero saved
  // positions, use them. Otherwise compute fresh layout.
  useEffect(() => {
    if (!graphQuery.data || !enrich) return;
    const layout = computeAutoLayout(graphQuery.data);

    const allZero = graphQuery.data.steps.every((s) => Number(s.position_x) === 0 && Number(s.position_y) === 0);

    const stepNodes: Node[] = graphQuery.data.steps.map((s) => {
      const pos = allZero
        ? layout.positionByStep.get(s.id) || { x: 0, y: 0 }
        : { x: Number(s.position_x), y: Number(s.position_y) };
      return {
        id: s.id,
        type: s.node_type,
        position: pos,
        data: {
          title: s.title,
          description: s.description || undefined,
          kpiCount: enrich.kpiByStep.get(s.id) || 0,
          ownerSeats: enrich.seatsByStep.get(s.id) || [],
          _row: s,
        },
      };
    });

    setNodes([...layout.laneNodes, ...stepNodes]);
    setEdges(graphQuery.data.edges.map((e) => ({
      id: e.id,
      source: e.source_step_id,
      target: e.target_step_id,
      sourceHandle: e.source_handle || undefined,
      targetHandle: e.target_handle || undefined,
      label: e.label || undefined,
      data: { _row: e },
    })));
    setDirty(false);
  }, [graphQuery.data, enrich]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
    if (changes.some((c) => c.type === 'position' || c.type === 'remove')) setDirty(true);
  }, []);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
    if (changes.some((c) => c.type === 'remove')) setDirty(true);
  }, []);

  const onConnect = useCallback((connection: Connection) => {
    setEdges((eds) => addEdge({ ...connection, id: newTempId(), data: {} }, eds));
    setDirty(true);
  }, []);

  const onSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    const first = params.nodes.find((n) => n.type !== 'lane');
    onSelectStepId?.(first?.id ?? null);
  }, [onSelectStepId]);

  const addNode = (type: ProcessNodeType) => {
    const id = newTempId();
    setNodes((nds) => [...nds, {
      id,
      type,
      position: { x: 200 + Math.random() * 200, y: 200 + Math.random() * 200 },
      data: { title: `New ${type}`, _new: true },
    }]);
    setDirty(true);
  };

  const handleAutoLayout = () => {
    if (!graphQuery.data) return;
    const layout = computeAutoLayout(graphQuery.data);
    setNodes((nds) => nds.map((n) => {
      if (n.type === 'lane') return n;
      const pos = layout.positionByStep.get(n.id);
      return pos ? { ...n, position: pos } : n;
    }));
    // Refresh lane background nodes (lane membership might have changed).
    setNodes((nds) => [
      ...layout.laneNodes,
      ...nds.filter((n) => n.type !== 'lane'),
    ]);
    setDirty(true);
  };

  const handleSave = async () => {
    const stepsPayload = nodes
      .filter((n) => n.type !== 'lane')
      .map((n) => {
        const isTemp = n.id.startsWith('tmp-');
        const row = (n.data as { _row?: { id: string } })._row;
        return {
          id: isTemp ? undefined : row?.id || n.id,
          tempId: isTemp ? n.id : undefined,
          nodeType: n.type as ProcessNodeType,
          title: (n.data as { title: string }).title,
          description: (n.data as { description?: string }).description || null,
          positionX: n.position.x,
          positionY: n.position.y,
        };
      });
    const edgesPayload = edges.map((e) => {
      const isTemp = e.id.startsWith('tmp-');
      return {
        id: isTemp ? undefined : e.id,
        sourceStepId: e.source,
        targetStepId: e.target,
        sourceHandle: e.sourceHandle || null,
        targetHandle: e.targetHandle || null,
        label: typeof e.label === 'string' ? e.label : null,
      };
    });
    await saveMutation.mutateAsync({ steps: stepsPayload, edges: edgesPayload });
    setDirty(false);
  };

  if (graphQuery.isLoading) {
    return <div className="flex-1 flex items-center justify-center text-muted-foreground">Loading graph…</div>;
  }
  if (graphQuery.isError) {
    return <div className="flex-1 flex items-center justify-center text-destructive">Failed to load graph.</div>;
  }

  return (
    <div className="relative flex-1 h-full">
      <div className="absolute top-2 left-2 z-10 flex gap-1">
        <Button size="sm" variant="outline" onClick={() => addNode('procedure')}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Procedure
        </Button>
        <Button size="sm" variant="outline" onClick={() => addNode('decision')}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Decision
        </Button>
        <Button size="sm" variant="outline" onClick={() => addNode('subprocess')}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Subprocess
        </Button>
        <Button size="sm" variant="outline" onClick={() => addNode('start')}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Start
        </Button>
        <Button size="sm" variant="outline" onClick={() => addNode('end')}>
          <Plus className="h-3.5 w-3.5 mr-1" /> End
        </Button>
        <Button size="sm" variant="outline" onClick={handleAutoLayout} title="Recompute LR layout grouped by accountable seat">
          <LayoutGrid className="h-3.5 w-3.5 mr-1" /> Auto-layout
        </Button>
      </div>
      <div className="absolute top-2 right-2 z-10">
        <Button size="sm" onClick={handleSave} disabled={!dirty || saveMutation.isPending}>
          <Save className="h-3.5 w-3.5 mr-1" />
          {saveMutation.isPending ? 'Saving…' : dirty ? 'Save graph' : 'Saved'}
        </Button>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
      >
        <Background gap={16} />
        <Controls />
        <MiniMap pannable zoomable />
      </ReactFlow>
    </div>
  );
}
